import { api } from './client';

/**
 * ============================================================================
 * THE MONEY CLIENT — nine routes, read-only, and NOT ONE FIGURE COMPUTED HERE
 * ============================================================================
 *
 * It mirrors the wire structs in `backend/internal/personalarea/{money_handlers,documents}.go`,
 * which are pinned BOTH WAYS to `api/openapi.yaml` by `wire_spec_test.go`. If a field
 * changes it changes there first, then here.
 *
 *   GET /pa/money/contracts     — the contract picker (§2.3)
 *   GET /pa/money/dashboard     — §3 in ONE read: balance, availability, the notice
 *   GET /pa/money/account-state — §6 «Состояние счёта»
 *   GET /pa/money/fund-usage    — §9 «Расход средств», one billing month
 *   GET /pa/money/payments      — §22, corrections included
 *   GET /pa/money/promises      — §23
 *   GET /pa/money/installments  — §20, view-only
 *   GET /pa/documents           — §19, portal-visible TYPES only (D211)
 *       /pa/documents/{id}/file — the bytes (D216). Followed as a LINK, never fetched
 *                                 here: see `download_url` below.
 *
 * ⚠ MONEY IS AN INTEGER OF MINOR UNITS ON EVERY FIELD, and this module does not convert
 * it. `src/format.ts` holds the one arithmetic-free conversion to a displayed figure, and
 * `src/money-arithmetic.test.ts` fails if any module in this app performs arithmetic on a
 * money-bearing field. The reason is not style: the figure that restores a suspended
 * subscriber's service has been wrong three times in this project, each time on a surface
 * that re-derived it (D64/D74, D67, and the per-Account share beside a contract-wide
 * gate). The portal LABELS the engine's figures; it never produces one.
 */

/** The house list envelope: `{"items","total"}`. */
export interface PAList<T> {
  items: T[];
  total: number;
}

/** One row of the contract picker — `PAContract`. */
export interface PAContract {
  id: number;
  number: string;
  /** Per-Contract (§2.3: «each Contract carries its own currency»). */
  currency: string;
  /** The code the subscriber quotes at an OSMP terminal or the office counter. */
  payment_code: string;
  /** The servicing Operator's id. The NAME is another domain's and is not resolved here. */
  operator_id: number | null;
  balance: number;
  /** `contract.default_in_personal_area` — which Contract the portal opens on. */
  is_default: boolean;
  /** "active" | "terminated". A deleted or archived Contract never appears. */
  status: string;
}

/**
 * The decomposed availability — `PAMoney`. All seven terms are always present: «0 held»
 * and «I do not know what is held» are different facts, so nothing here is optional.
 */
export interface PAMoney {
  balance: number;
  credit_limit: number;
  /** The ACTIVE promise's unrepaid part. It counts into availability, NOT into balance. */
  promise_remainder: number;
  promise_expires_at: string | null;
  /** The installment SHIELD — the fourth availability term (Appendix M §3.18 «M3»). */
  installment: number;
  availability: number;
  /** Held for the coming renewal (Appendix I §2.15). */
  reserved: number;
  reserved_boundary_at: string | null;
  /** `availability − reserved`, computed BY THE SERVER. */
  spendable: number;
}

/** §3's recommended payment and the MODE that produced it — `PARecommended`. */
export interface PARecommended {
  amount: number;
  mode: number;
}

/** One suspended Account inside the notice. It carries NO money field, by design. */
export interface PASuspendedAccount {
  account_id: number;
  login: string;
  /** The cycle's own reason code; the portal owns the Russian wording. */
  reason: string;
  suspended_at: string | null;
}

/**
 * THE SUSPENSION NOTICE — `PASuspension`.
 *
 * ⚠ `amount` IS THE FIGURE THAT RESTORES SERVICE AND IT IS CONTRACT-WIDE. It is
 * `billing.RecommendedPaymentModeQuote(contract, RecommendModeResume).Amount` — the same
 * number the OSMP terminal, the payment window and the suspension SMS quote for this
 * Contract at this instant. The screen PRINTS it. It does not adjust it, round it, or
 * divide it by the number of accounts, and `requirement`/`floor` exist so the screen can
 * SAY WHAT THE FIGURE IS without deriving it from them.
 */
export interface PASuspension {
  /** The exact top-up that lifts every suspension on this Contract. Never zero. */
  amount: number;
  /** The GROSS the engine is owed — the PACK, not the fee. `amount` ≠ requirement − money. */
  requirement: number;
  /** "" | "missed_promise" | "withheld" — which requirement decided `amount`. */
  floor: string;
  accounts: PASuspendedAccount[];
}

/** One row of the dashboard's account table — `PAAccount`. */
export interface PAAccount {
  account_id: number;
  login: string;
  /** The Appendix C §2.2 code; the portal owns the Russian labels. */
  status: number;
  /** The DERIVED never-activated marker (§2.8) — status 10 reads as «отключено» without it. */
  awaiting_activation: boolean;
  /**
   * THE TARIFF-GROUP ROUTING KEY (2026-08-16). True when this Account has no primary
   * tariff and activating it means first CHOOSING a plan out of its activation tariff
   * group. Carried on every never-activated row — including one whose money could not
   * be read, because it is a fact about the configuration and not about the money.
   */
  tariff_choice_required: boolean;
  tariff_name: string;
  billing_model: string;
  /** The TARIFF's list price. It carries NO recurring-method discount — `cycle_cost` does. */
  subscription_fee: number | null;
  bandwidth_kbps: number | null;
  /**
   * The engine's own price for ONE cycle of this Account, discount applied.
   *
   * ⚠ UNDER MODEL A IT IS THE MONTH, NOT THE DAY (`billing.recommendCycleCost`), and no
   * exported engine definition of a Model-A daily figure exists. `billing_model` is on
   * the row so the screen can label which of the two it is showing; inventing the daily
   * figure here is precisely what this wave forbids.
   */
  cycle_cost: number | null;
  next_charge_at: string | null;
  coverage_until: string | null;
  /** "paid" | "unpaid" | "suspended"; empty when the Account has no cycle at all. */
  cycle_state: string;
  /**
   * THE ACTIVATION GATE's own requirement — `max(minimum activation balance,
   * first-activation charge)` — and what must be PAID IN to reach it. Present only on a
   * row whose `awaiting_activation` is true, and NULL (never 0) when the gate could not
   * be evaluated.
   *
   * ⚠ IT IS THE ONLY FIGURE ON THIS SCREEN THAT ACTIVATES HIM. `cycle_cost` above and the
   * contract's `recommended` can both be strictly BELOW it — measured so on the demo seed,
   * where both read 500,00 against a 1 000,00 minimum — so paying either leaves the account
   * switched off. That is «a recommendation below what releases the service», the D64/D67
   * class, on the state EVERY new and EVERY imported Account passes through.
   */
  activation_required: number | null;
  activation_shortfall: number | null;
}

/**
 * Why the dashboard shows no figure on a contract that IS suspended — `""` when nothing is
 * suspended or the notice is present.
 *
 * ⚠ IT EXISTS BECAUSE THE SCREEN FELL SILENT. The notice is gated on a NON-ZERO amount
 * («внесите 0,00» is simply false), and three ordinary paths reach zero while the status
 * column is still red: the second between the suspension and the `balance.changed` that
 * lifts it; an account with NO CYCLE AT ALL — every imported status-1 account at cutover —
 * and a tariff the engine cannot price, which it drops from the quote with a warning. In
 * all three the row said «Приостановлена за неоплату» with no notice and no sentence,
 * indistinguishable from a working page.
 */
export type PASuspendedWithoutFigure = '' | 'awaiting_resume' | 'amount_unavailable';

/** GET /pa/money/dashboard — `PADashboard`. */
export interface PADashboard {
  contract: PAContract;
  money: PAMoney;
  recommended: PARecommended;
  /** NULL when nothing is financially suspended, or when the requirement is already met. */
  suspension: PASuspension | null;
  suspended_without_figure: PASuspendedWithoutFigure;
  accounts: PAAccount[];
}

/** GET /pa/money/account-state — `PAAccountState` (§6). */
export interface PAAccountState {
  contract_id: number;
  currency: string;
  balance: number;
  /** The month the two totals cover, YYYY-MM. */
  period: string;
  payments_received: number;
  expenses: number;
}

/** One line of §9's fund-usage table — `PACharge`. */
export interface PACharge {
  id: number;
  account_id: number;
  /** "subscription_fee" | "usage" | "service" | … — §9's two blocks group on this. */
  charge_type: string;
  name: string;
  /** The engine's own metered quantity, VERBATIM as a decimal string. Never re-rounded. */
  quantity: string | null;
  amount: number;
  period: string;
  calculated_at: string;
  coverage_from: string | null;
  coverage_to: string | null;
}

/** GET /pa/money/fund-usage — `PAFundUsage` (§9). */
export interface PAFundUsage {
  contract_id: number;
  account_id: number | null;
  period: string;
  /** §9's «Общая сумма списания» over the WHOLE month — the server's own sum. */
  total_charged: number;
  items: PACharge[];
  total: number;
}

/** One row of §22's payment history — `PAPayment`. */
export interface PAPayment {
  id: number;
  amount: number;
  currency: string;
  number: string;
  /** "posted" | "confirmed" | "cancelled". §22's «Принят» covers the first two. */
  state: string;
  /**
   * "credit" | "reversal" | "debit".
   *
   * ⚠ IT IS WHAT MAKES A CORRECTION LEGIBLE. The corrections §2.5's last paragraph asks
   * for — a staff cancellation, an amount correction, a transfer, an acquirer refund —
   * are ROWS IN THIS SAME LIST; there is no separate corrections table. Without the
   * direction a −200 reversal is indistinguishable from a payment of −200.
   */
  direction: string;
  /** "payonline" | "osmp" | "manual" | "registry" | … — §22's «Способ платежа». */
  source: string;
  reverses_payment_id: number | null;
  corrects_payment_id: number | null;
  /** "annul" | "amount" | "transfer" | "restore" | "". */
  correction_kind: string;
  /** "none" | "requested" | "settled" | "failed" — rides on the REVERSAL row. */
  acquirer_refund_state: string;
  /** The payer's own date (§22's «Дата»). */
  received_at: string;
  /** When billing booked it. It differs for a registry import and a late office entry. */
  posted_at: string;
  cancelled_at: string | null;
  /** The SYSTEM took it from the saved card on its own initiative. */
  auto_charge: boolean;
}

/** One row of §23's promised-payment history — `PAPromise`. */
export interface PAPromise {
  id: number;
  amount: number;
  repaid_amount: number;
  /** The unrepaid part — the engine's own `Promise.Remainder()`, clamped there. */
  remainder: number;
  granted_at: string;
  expires_at: string;
  /** The engine's four states: "active" | "expired" | "repaid" | "cancelled". */
  state: string;
  taken_by_subscriber: boolean;
}

/** One row of §20's schedule table — `PAInstallmentStep`. */
export interface PAInstallmentStep {
  seq: number;
  /** A CIVIL date, YYYY-MM-DD, so no timezone can move it. */
  due_date: string;
  amount: number;
  applied: boolean;
  applied_at: string | null;
}

/** One installment plan — `PAInstallment` (§20). */
export interface PAInstallment {
  id: number;
  subject: string;
  plan_name: string;
  duration_months: number;
  /** The FULL cost, debited at assignment. */
  price_amount: number;
  initial_amount: number;
  /**
   * THE LIVE SHIELD — the fourth availability term, decaying one step per anniversary.
   *
   * ⚠ IT IS NOT A DEBT AND IS NOT PAID DOWN BY PAYMENTS (Appendix M §3.18 «M3», which
   * STRUCK the earlier claim that it was). The whole price was taken on day one; what
   * remains is calendar-driven protection. Labelling it «осталось выплатить» would tell
   * the subscriber he owes money already taken from him, and he would pay it twice in all
   * but name.
   */
  remaining_amount: number;
  live: boolean;
  assigned_at: string;
  closed_at: string | null;
  closed_reason: string;
  steps: PAInstallmentStep[];
}

/** One row of §19's document list — `PADocument`. */
export interface PADocument {
  id: number;
  number: string;
  template_name: string;
  class: string;
  issue_date: string;
  period_start: string | null;
  period_end: string | null;
  amount: number;
  vat_amount: number;
  currency: string;
  payable: boolean;
  paid_at: string | null;
  size_bytes: number;
  filename: string;
  /**
   * THE SERVER'S OWN PATH TO THE BYTES, and the screen follows it as given.
   *
   * ⚠ IT IS NEVER REBUILT FROM `id`. The download re-checks BOTH predicates the list
   * applied — this subscriber owns the document's contract, AND its template is
   * portal-visible (D211) — so a client that constructed `/pa/documents/{id}/file` for a
   * row it invented would be asking a question the server refuses, and a client that
   * constructed one for a row it was NOT given would be enumerating. Following the
   * server's link keeps the set of reachable documents exactly the set it offered.
   */
  download_url: string;
}

/** The `state` filter of §19: «Все» / «Оплаченные» / «Не оплаченные». */
export type DocumentState = '' | 'paid' | 'unpaid';

/**
 * ============================================================================
 * THE SERVER'S PAGE SIZES — mirrored, because they are not a caller's choice
 * ============================================================================
 *
 * Three of these lists are PAGED by the server at a fixed ceiling and the caller supplies
 * only `?offset=`: an unbounded page on a subscriber-facing listener is a way to make the
 * database do work an attacker chose the size of. So the client cannot ask how big a page
 * is — it has to know, and «know» here means MIRROR, which is a second copy of a constant
 * and therefore a thing that drifts.
 *
 * It does not drift silently: `src/api/page-size.test.ts` reads the Go constants out of
 * `backend/internal/personalarea/money_handlers.go` and fails if either number moves. That
 * is the same shape as the OpenAPI pin next door, and for the same reason — a mismatch here
 * is invisible by construction (the pager would simply skip or repeat rows, and every
 * fixture in this suite is smaller than one page).
 *
 * ⚠ Until 2026-08-12 nothing in this SPA read `total` and nothing sent `offset`, so a
 * subscriber with 812 payments was shown the newest 500 and told nothing.
 */
export const PAYMENT_PAGE_SIZE = 500;
export const CHARGE_PAGE_SIZE = 500;
export const DOCUMENT_PAGE_SIZE = 200;

export function listContracts(): Promise<PAList<PAContract>> {
  return api.get<PAList<PAContract>>('/pa/money/contracts');
}

export function getDashboard(contractID: number): Promise<PADashboard> {
  return api.get<PADashboard>(`/pa/money/dashboard?contract_id=${contractID}`);
}

export function getAccountState(contractID: number): Promise<PAAccountState> {
  return api.get<PAAccountState>(`/pa/money/account-state?contract_id=${contractID}`);
}

export function getFundUsage(
  contractID: number,
  opts: { period?: string; accountID?: number | null; offset?: number } = {},
): Promise<PAFundUsage> {
  const q = new URLSearchParams({ contract_id: String(contractID) });
  if (opts.period) q.set('period', opts.period);
  if (opts.accountID) q.set('account_id', String(opts.accountID));
  if (opts.offset) q.set('offset', String(opts.offset));
  return api.get<PAFundUsage>(`/pa/money/fund-usage?${q.toString()}`);
}

export function listPayments(
  contractID: number,
  opts: { from?: string; to?: string; offset?: number } = {},
): Promise<PAList<PAPayment>> {
  const q = new URLSearchParams({ contract_id: String(contractID) });
  if (opts.from) q.set('from', opts.from);
  if (opts.to) q.set('to', opts.to);
  if (opts.offset) q.set('offset', String(opts.offset));
  return api.get<PAList<PAPayment>>(`/pa/money/payments?${q.toString()}`);
}

export function listPromises(contractID: number): Promise<PAList<PAPromise>> {
  return api.get<PAList<PAPromise>>(`/pa/money/promises?contract_id=${contractID}`);
}

export function listInstallments(contractID: number): Promise<PAList<PAInstallment>> {
  return api.get<PAList<PAInstallment>>(`/pa/money/installments?contract_id=${contractID}`);
}

export function listDocuments(
  contractID: number,
  opts: { from?: string; to?: string; state?: DocumentState; offset?: number } = {},
): Promise<PAList<PADocument>> {
  const q = new URLSearchParams({ contract_id: String(contractID) });
  if (opts.from) q.set('from', opts.from);
  if (opts.to) q.set('to', opts.to);
  if (opts.state) q.set('state', opts.state);
  if (opts.offset) q.set('offset', String(opts.offset));
  return api.get<PAList<PADocument>>(`/pa/documents?${q.toString()}`);
}

/**
 * itemsOf is the ONE place a list envelope is unwrapped.
 *
 * It tolerates a body with no `items` array — not to be forgiving about a server bug,
 * but because the alternative is a `TypeError` inside a render, which the subscriber
 * reads as a blank page with no explanation and which no error boundary in this app
 * would report any better. A missing array is an empty list plus whatever the browser
 * console says.
 */
export function itemsOf<T>(list: PAList<T> | null | undefined): T[] {
  return Array.isArray(list?.items) ? list.items : [];
}
